import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TextInput, TouchableOpacity, Alert, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import Button3D from '../components/Button3D';
import { useGuardiaEscolar } from '../hooks/useGuardiaEscolar';
import { leerKycDocs } from '../utils/kycDocs';
import { normalizarPlaca, revisarPlaca } from '../utils/placa';

/**
 * PERFIL DE RUTA DEL CHOFER ESCOLAR
 * ----------------------------------------------------------------------------
 * El chofer escolar declara qué colegios/rutas sirve para ser DESCUBRIBLE por
 * padres y directoras (búsqueda por ruta). Sin esto, la lista de choferes de un
 * colegio quedaba vacía para siempre. Guarda schools[] + schoolKeys[] + vehículo
 * + tarifas por zona en su /users/{uid}. No cobra nada hasta tener un servicio
 * activo (el trial de 30 días arranca cuando un padre lo inscribe).
 */
export default function SchoolDriverSetupScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [schoolsText, setSchoolsText] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [plate, setPlate] = useState('');
  const [seats, setSeats] = useState('');
  const [zoneA, setZoneA] = useState('3500');
  const [zoneB, setZoneB] = useState('4500');
  const [zoneC, setZoneC] = useState('5500');
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(true);

  useEffect(() => {
    if (!uid) { setHydrating(false); return; }
    let alive = true;
    firestoreService.getUserProfile(uid).then((p) => {
      if (!alive || !p) { setHydrating(false); return; }
      if (Array.isArray(p.schools)) setSchoolsText(p.schools.join(', '));
      if (p.vehicle) setVehicle(String(p.vehicle));
      if (p.plate) setPlate(String(p.plate));
      if (p.seats) setSeats(String(p.seats));
      if (p.zones) {
        if (p.zones.A != null) setZoneA(String(p.zones.A));
        if (p.zones.B != null) setZoneB(String(p.zones.B));
        if (p.zones.C != null) setZoneC(String(p.zones.C));
      }
      setHydrating(false);
    }).catch(() => setHydrating(false));
    return () => { alive = false; };
  }, [uid]);

  const handleSave = async () => {
    if (!uid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para publicar tu ruta.');
      return;
    }
    const schools = schoolsText.split(',').map((s) => s.trim()).filter(Boolean);
    if (schools.length === 0) {
      Alert.alert('Colegios requeridos', 'Escribe al menos un colegio/ruta que sirvas (sepáralos con comas).');
      return;
    }
    // ── LA PLACA ───────────────────────────────────────────────────────────
    // Esta pantalla guardaba `plate: plate.trim()` SIN NINGUNA comprobación:
    // aquí se podía dejar en blanco o escribir cualquier cosa. Y es la peor de
    // las cuatro pantallas donde se pide placa, porque lo que publica es una
    // ruta de NIÑOS: el padre y la directora buscan en el directorio y la placa
    // es lo que les dice qué vehículo va a recoger a su hijo.
    const revisionPlaca = revisarPlaca(plate);
    if (!revisionPlaca.ok) {
      Alert.alert(revisionPlaca.titulo, revisionPlaca.motivo);
      return;
    }
    // PAPEL DE BUENA CONDUCTA obligatorio también para el chofer escolar
    // (transporta niños). Sin él, o si el admin lo rechazó, NO puede publicar
    // su ruta ni ser descubrible por padres/directora.
    try {
      const me = await firestoreService.getUserProfile(uid);
      const status = String(me?.conductPaperStatus || '');
      // Lectura tolerante: los documentos viejos guardaron las fotos como campos
        // sueltos con punto en el nombre, no como mapa. Ver utils/kycDocs.ts.
        const kycDocs = leerKycDocs(me);
      const hasPaper =
        status === 'APPROVED' ||
        status === 'PENDING_REVIEW' ||
        Object.keys(kycDocs).some((k) => k.toLowerCase().includes('conducta') && !!kycDocs[k]);
      if (status === 'REJECTED' || !hasPaper) {
        Alert.alert(
          'Falta tu Papel de Buena Conducta',
          status === 'REJECTED'
            ? 'Tu Papel de Buena Conducta fue rechazado. Sube uno vigente y sin antecedentes para publicar tu ruta escolar.'
            : 'Para transportar estudiantes debes subir tu Papel de Buena Conducta (Procuraduría, sin antecedentes).',
          [
            { text: 'Más tarde', style: 'cancel' },
            { text: 'Subir ahora', onPress: () => router.push('/kyc-documents' as any) },
          ]
        );
        return;
      }
    } catch (e) {
      console.warn('No se pudo verificar el papel de conducta del chofer escolar:', e);
    }
    const toNum = (s: string, d: number) => {
      const n = parseInt(String(s).replace(/[^0-9]/g, ''), 10);
      return Number.isFinite(n) && n > 0 ? n : d;
    };
    setLoading(true);
    try {
      await firestoreService.setSchoolDriverProfile(uid, {
        schools,
        vehicle: vehicle.trim(),
        // Ya normalizada y comprobada arriba: mayúsculas, sin guiones.
        plate: revisionPlaca.placa,
        seats: seats.trim(),
        zones: { A: toNum(zoneA, 3500), B: toNum(zoneB, 4500), C: toNum(zoneC, 5500) },
      });
      Alert.alert(
        '¡Ruta publicada!',
        'Ya apareces en la búsqueda de los padres y directoras de esos colegios. Solo empiezas a pagar cuando un padre te contrata.',
        [{ text: 'Entendido', onPress: () => (router.canGoBack() ? router.back() : router.replace('/(tabs)/map')) }]
      );
    } catch (e: any) {
      Alert.alert('Error', 'No pudimos publicar tu ruta. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * `mayusculas`: abre el teclado del telefono ya en MAYUSCULA.
   *
   * Lo pidio el fundador para la placa: «el teclado del movil se pondra en
   * mayuscula desde que el usuario este donde se llena la casilla». Los otros
   * dos sitios donde se pide placa (el KYC del chofer y la flotilla) ya lo
   * hacian; este se habia quedado fuera porque comparte este ayudante con
   * campos de texto normales, donde forzar mayusculas seria una molestia.
   *
   * Es la PRIMERA de dos capas: `normalizarPlaca` convierte a mayuscula en cada
   * tecla igual. Hacen falta las dos, porque `autoCapitalize` es una SUGERENCIA
   * al teclado y algunos teclados de Android la ignoran (sobre todo escribiendo
   * deslizando). Sin la segunda capa, ese teclado dejaria pasar minusculas.
   */
  const input = (
    label: string,
    value: string,
    setter: (v: string) => void,
    placeholder: string,
    keyboard?: 'default' | 'number-pad',
    multiline?: boolean,
    mayusculas?: boolean,
    maxLength?: number
  ) => (
    <View style={{ marginBottom: 16 }}>
      <Text style={[styles.label, { color: colors.text }]}>{label}</Text>
      <TextInput
        style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }, multiline && { height: 72, textAlignVertical: 'top' }, mayusculas && { letterSpacing: 2, fontWeight: 'bold' }]}
        value={value}
        onChangeText={setter}
        placeholder={placeholder}
        placeholderTextColor={colors.textSecondary}
        keyboardType={keyboard === 'number-pad' ? 'number-pad' : 'default'}
        autoCapitalize={mayusculas ? 'characters' : 'sentences'}
        autoCorrect={mayusculas ? false : undefined}
        maxLength={maxLength}
        multiline={!!multiline}
      />
    </View>
  );

  // Vertical escolar oculto: ver VERTICAL_ESCOLAR_VISIBLE en featureFlags.ts
  const escolarOculto = useGuardiaEscolar();
  if (escolarOculto) return null;

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => (router.canGoBack() ? router.back() : router.replace('/(tabs)/map'))} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Mi Ruta Escolar</Text>
        <View style={{ width: 32 }} />
      </View>

      {hydrating ? (
        <ActivityIndicator color={colors.primary} style={{ marginTop: 40 }} />
      ) : (
        <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
          <View style={[styles.infoBox, { backgroundColor: 'rgba(0,200,83,0.08)', borderColor: 'rgba(0,150,136,0.3)' }]}>
            <Ionicons name="bus" size={20} color="#00796B" style={{ marginRight: 8 }} />
            <Text style={[styles.infoText, { color: colors.text }]}>
              Publica los colegios/rutas que sirves para que padres y directoras te encuentren. Solo pagas cuando ya tienes un servicio contratado.
            </Text>
          </View>

          {input('Colegios que sirves (sepáralos con comas)', schoolsText, setSchoolsText, 'Ej. Colegio Loyola, Liceo San Juan', 'default', true)}
          {input('Vehículo', vehicle, setVehicle, 'Ej. Minibús Toyota Hiace', 'default')}
          {/* La placa se limpia mientras se escribe (mayúsculas, sin guiones ni
              espacios) para que lo que se ve sea lo que se guarda. */}
          {/* mayusculas=true: el teclado se abre en MAYUSCULA, igual que en el
              KYC del chofer y en la flotilla. maxLength 9 = 2 letras + 7 digitos. */}
          {input('Placa', plate, (v: string) => setPlate(normalizarPlaca(v)), 'Ej. A123456', 'default', false, true, 9)}
          {input('Capacidad (asientos)', seats, setSeats, 'Ej. 15 asientos', 'default')}

          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>TARIFAS MENSUALES POR ZONA (RD$)</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>{input('Zona A (0-5km)', zoneA, setZoneA, '3500', 'number-pad')}</View>
            <View style={{ flex: 1 }}>{input('Zona B (5-10km)', zoneB, setZoneB, '4500', 'number-pad')}</View>
            <View style={{ flex: 1 }}>{input('Zona C (+10km)', zoneC, setZoneC, '5500', 'number-pad')}</View>
          </View>

          <View style={{ marginTop: 10 }}>
            <Button3D title={loading ? 'Publicando…' : 'Publicar mi ruta'} color="#00C853" onPress={handleSave} />
          </View>
          <View style={{ height: 40 }} />
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
  },
  backBtn: { padding: 4 },
  headerTitle: { fontSize: 18, fontWeight: 'bold' },
  container: { padding: 20 },
  infoBox: { flexDirection: 'row', alignItems: 'flex-start', padding: 12, borderRadius: 12, borderWidth: 1, marginBottom: 20 },
  infoText: { flex: 1, fontSize: 12.5, lineHeight: 18 },
  label: { fontSize: 13, fontWeight: '600', marginBottom: 6 },
  input: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 12, fontSize: 15 },
  sectionTitle: { fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 10, marginTop: 4 },
});
